import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
const processSteps = ['System audit and data collection', 'Energy performance assessment', 'TM44 report with recommendations', 'Remediation support if needed', 'Compliance certificate issued'];
export function TM44() {
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-2317">
      {/* Hero */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-2318">
        <div className="container mx-auto px-6" data-id="element-2319">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-2320">
            <div className="lg:col-span-7" data-id="element-2321">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-2322">
                Services / TM44 compliance
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark" data-id="element-2323">
                TM44 inspections explained simply
              </motion.h1>
            </div>
            <div className="lg:col-span-5 lg:pt-12" data-id="element-2324">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2325">
                If your commercial building has air conditioning totalling over
                12kW, you need a TM44 inspection every five years. We handle the
                inspection, the report, and any remediation needed.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* What is TM44 */}
      <section className="bg-white py-24" data-id="element-2326">
        <div className="container mx-auto px-6" data-id="element-2327">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-2328">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2329">
              <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-2330">
                What is a TM44 inspection?
              </h2>
              <p className="text-primary-dark/70 leading-relaxed text-lg" data-id="element-2331">
                A TM44 inspection is a mandatory energy efficiency assessment
                for commercial air conditioning systems. It is designed to
                improve efficiency, reduce energy consumption, and lower carbon
                emissions. Instead of legal jargon, think of it as an MOT for
                your building's cooling system to ensure it isn't wasting energy
                or costing you more than it should.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-primary-light/30 p-10 rounded-3xl border border-primary-dark/5" data-id="element-2332">
              <h3 className="text-xl font-serif mb-6 flex items-center gap-3" data-id="element-2333">
                <AlertTriangle className="w-6 h-6 text-accent" data-id="element-2334" />
                Key facts
              </h3>
              <ul className="space-y-4" data-id="element-2335">
                <li className="flex items-start gap-3" data-id="element-2336">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" data-id="element-2337" />
                  <span data-id="element-2338">Required by law every 5 years</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2339">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" data-id="element-2340" />
                  <span data-id="element-2341">Applies to systems over 12kW combined capacity</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2342">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" data-id="element-2343" />
                  <span data-id="element-2344">Fines up to £5,000 per system for non-compliance</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2345">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" data-id="element-2346" />
                  <span data-id="element-2347">
                    Must be conducted by an accredited energy assessor
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who needs it */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-2348">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-2349">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2350">
            Do I need a TM44?
          </motion.h2>
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-primary-dark/5" data-id="element-2351">
            <div className="space-y-6" data-id="element-2352">
              <div className="flex items-center gap-4" data-id="element-2353">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0" data-id="element-2354" />
                <span className="text-lg" data-id="element-2355">
                  Do you own or manage a non-domestic building?
                </span>
              </div>
              <div className="flex items-center gap-4" data-id="element-2356">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0" data-id="element-2357" />
                <span className="text-lg" data-id="element-2358">Does it have air conditioning?</span>
              </div>
              <div className="flex items-center gap-4" data-id="element-2359">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0" data-id="element-2360" />
                <span className="text-lg" data-id="element-2361">
                  Is the total cooling capacity over 12kW?
                </span>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-primary-dark/10 text-center" data-id="element-2362">
              <p className="text-xl font-medium text-primary-dark" data-id="element-2363">
                If you answered yes to all three,{' '}
                <span className="text-accent" data-id="element-2364">you need a TM44 inspection.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="bg-white py-24" data-id="element-2365">
        <div className="container mx-auto px-6" data-id="element-2366">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2367">
            Our TM44 service
          </motion.h2>
          <div className="grid md:grid-cols-5 gap-6" data-id="element-2368">
            {processSteps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="bg-[#E8F0F8] p-6 rounded-2xl text-center relative" data-id="element-2369">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-accent font-bold mx-auto mb-4 shadow-sm" data-id="element-2370">
                  {index + 1}
                </div>
                <p className="font-medium text-sm" data-id="element-2371">{step}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* F-Gas crossover */}
      <section className="bg-[#D4DDE8] py-24" data-id="element-2372">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-2373">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2374">
            TM44 and F-Gas: what is the difference?
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-2375">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-10 rounded-3xl shadow-sm" data-id="element-2376">
              <h3 className="text-2xl font-serif mb-4 text-accent" data-id="element-2377">TM44</h3>
              <p className="text-primary-dark/70 mb-6" data-id="element-2378">
                Focuses on energy performance and efficiency.
              </p>
              <ul className="space-y-3 text-sm font-medium" data-id="element-2379">
                <li data-id="element-2380">• Required every 5 years</li>
                <li data-id="element-2381">• For systems over 12kW</li>
                <li data-id="element-2382">• Identifies energy waste</li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-10 rounded-3xl shadow-sm" data-id="element-2383">
              <h3 className="text-2xl font-serif mb-4 text-accent" data-id="element-2384">F-Gas</h3>
              <p className="text-primary-dark/70 mb-6" data-id="element-2385">
                Focuses on environmental safety and refrigerant leaks.
              </p>
              <ul className="space-y-3 text-sm font-medium" data-id="element-2386">
                <li data-id="element-2387">• Required every 3-12 months</li>
                <li data-id="element-2388">• For systems containing fluorinated gases</li>
                <li data-id="element-2389">• Prevents harmful emissions</li>
              </ul>
            </motion.div>
          </div>
          <p className="text-center mt-10 text-primary-dark/70 font-medium" data-id="element-2390">
            AccuTemp handles both. We keep your building fully compliant across
            all regulations.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8" data-id="element-2391">
            <Link to="/services/maintenance" className="inline-flex items-center gap-2 text-accent hover:text-primary-dark font-medium transition-colors" data-id="element-2392">
              Explore our maintenance contracts{' '}
              <ArrowRight className="w-4 h-4" data-id="element-2393" />
            </Link>
            <span className="hidden md:inline text-primary-dark/20" data-id="element-2394">|</span>
            <Link to="/knowledge-hub/compliance" className="inline-flex items-center gap-2 text-accent hover:text-primary-dark font-medium transition-colors" data-id="element-2395">
              Read our compliance guide <ArrowRight className="w-4 h-4" data-id="element-2396" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-dark py-24 text-center" data-id="element-2397">
        <div className="container mx-auto px-6" data-id="element-2398">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-2399">
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-10" data-id="element-2400">
              Check your TM44 status
            </h2>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2401">
              Book an inspection
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2402" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}